import { ChangeEvent, FC, useEffect, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import clsx from 'clsx'
import { isNotEmpty } from '../../../../../_metronic/helpers'
import { initialResellerGroup, ResellerGroup } from '../core/_models'
import { useListView } from '../core/ListViewProvider'
import { ResellerGroupsListLoading } from '../components/loading/ResellerGroupsListLoading'
import { createResellerGroup, updateResellerGroup } from '../core/_requests'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { FormattedMessage } from 'react-intl'
import axios from 'axios'
import Select from 'react-select'

type Props = {
  isResellerGroupLoading: boolean
  resellergroup: ResellerGroup
}

// Validation Schema
const editResellerGroupSchema = Yup.object().shape({
  name: Yup.string().required('Group name is required'),
  discount_type: Yup.string().oneOf(['percentage', 'fixed']).required('Discount type is required'),
  discount_value: Yup.number().required('Discount value is required').min(0, 'Discount value must be positive'),
  can_create_sub_resellers: Yup.boolean(),
  sub_reseller_limit: Yup.number().min(0, 'Sub reseller limit must be at least 0'),
  can_sub_reseller_create_subs: Yup.boolean(),
  status: Yup.string().oneOf(['active', 'inactive']).required('Status is required'),
  notes: Yup.string().nullable(),
})

const ResellerGroupEditModalForm: FC<Props> = ({ resellergroup, isResellerGroupLoading }) => {
  const { setItemIdForUpdate } = useListView()
  const { refetch } = useQueryResponse()

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch()
    }
    setItemIdForUpdate(undefined)
  }

  const formik = useFormik({
    initialValues: {
      ...resellergroup,
      name: resellergroup.name || initialResellerGroup.name,
      discount_type: resellergroup.discount_type || initialResellerGroup.discount_type,
      discount_value: resellergroup.discount_value || initialResellerGroup.discount_value,
      can_create_sub_resellers: resellergroup.can_create_sub_resellers || initialResellerGroup.can_create_sub_resellers,
      sub_reseller_limit: resellergroup.sub_reseller_limit || initialResellerGroup.sub_reseller_limit,
      can_sub_reseller_create_subs: resellergroup.can_sub_reseller_create_subs || initialResellerGroup.can_sub_reseller_create_subs,
      status: resellergroup.status || initialResellerGroup.status,
      notes: resellergroup.notes || initialResellerGroup.notes,
    },
    validationSchema: editResellerGroupSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (isNotEmpty(values.id)) {
          await updateResellerGroup(values)
        } else {
          await createResellerGroup(values)
        }
      } catch (ex) {
        console.error(ex)
      } finally {
        setSubmitting(false)
        cancel(true)
      }
    },
  })

  return (
    <>
      <form id='kt_modal_add_resellergroup_form' className='form' onSubmit={formik.handleSubmit} noValidate>
        <div className='d-flex flex-column scroll-y me-n7 pe-7' id='kt_modal_add_resellergroup_scroll' data-kt-scroll='true' data-kt-scroll-activate='{default: false, lg: true}' data-kt-scroll-max-height='auto' data-kt-scroll-dependencies='#kt_modal_add_resellergroup_header' data-kt-scroll-wrappers='#kt_modal_add_resellergroup_scroll' data-kt-scroll-offset='300px'>
          {/* Form Fields */}
          <div className="row">
            {/* Group Name */}
            <div className='col-lg-6 fv-row mb-7'>
              <label className='required fw-bold fs-6 mb-2'><FormattedMessage id='RESELLERGROUP.FORM.INPUT.GROUPNAME' defaultMessage='Group Name' /></label>
              <input
                {...formik.getFieldProps('name')}
                className={clsx(
                  'form-control form-control-solid mb-3 mb-lg-0',
                  { 'is-invalid': formik.touched.name && formik.errors.name },
                  { 'is-valid': formik.touched.name && !formik.errors.name }
                )}
                type='text'
                autoComplete='off'
                disabled={formik.isSubmitting || isResellerGroupLoading}
              />
            </div>

            {/* Discount Type */}
            <div className='col-lg-6 fv-row mb-7'>
              <label className='required fw-bold fs-6 mb-2'><FormattedMessage id='RESELLERGROUP.FORM.INPUT.DISCOUNTTYPE' defaultMessage='Discount Type' /></label>
              <select
                {...formik.getFieldProps('discount_type')}
                className={clsx(
                  'form-select form-control',
                  { 'is-invalid': formik.touched.discount_type && formik.errors.discount_type },
                  { 'is-valid': formik.touched.discount_type && !formik.errors.discount_type }
                )}
                disabled={formik.isSubmitting || isResellerGroupLoading}
              >
                <option value=''>Select</option>
                <option value='percentage'>Percentage</option>
                <option value='fixed'>Fixed</option>
              </select>
            </div>

            {/* Discount Value */}
            <div className='col-lg-6 fv-row mb-7'>
              <label className='required fw-bold fs-6 mb-2'><FormattedMessage id='RESELLERGROUP.FORM.INPUT.DISCOUNTVALUE' defaultMessage='Discount Value' /></label>
              <input
                {...formik.getFieldProps('discount_value')}
                className={clsx(
                  'form-control form-control-solid mb-3 mb-lg-0',
                  { 'is-invalid': formik.touched.discount_value && formik.errors.discount_value },
                  { 'is-valid': formik.touched.discount_value && !formik.errors.discount_value }
                )}
                type='number'
                autoComplete='off'
                disabled={formik.isSubmitting || isResellerGroupLoading}
              />
            </div>

            {/* Can Add Sub-Resellers */}
            <div className='col-lg-6 fv-row mb-7'>
              <label className='fw-bold fs-6 mb-2'><FormattedMessage id='RESELLERGROUP.FORM.INPUT.CANADDSUBRESELLERS' defaultMessage='Can Add Sub-Resellers' /></label>
              <select
                {...formik.getFieldProps('can_create_sub_resellers')}
                className='form-select form-control'
                disabled={formik.isSubmitting || isResellerGroupLoading}
              >
                <option value='true'>Yes</option>
                <option value='false'>No</option>
              </select>
            </div>

            {/* Sub Reseller Limit */}
            <div className='col-lg-6 fv-row mb-7'>
              <label className='fw-bold fs-6 mb-2'><FormattedMessage id='RESELLERGROUP.FORM.INPUT.SUBRESELLERLIMIT' defaultMessage='Sub Reseller Limit' /></label>
              <input
                {...formik.getFieldProps('sub_reseller_limit')}
                className='form-control form-control-solid'
                type='number'
                autoComplete='off'
                disabled={formik.isSubmitting || isResellerGroupLoading}
              />
            </div>

            {/* Can Sub-Reseller Create Subs */}
            <div className='col-lg-6 fv-row mb-7'>
              <label className='fw-bold fs-6 mb-2'><FormattedMessage id='RESELLERGROUP.FORM.INPUT.CANSUBRESELLERCREATESUBS' defaultMessage='Can Sub-Reseller Create Subs' /></label>
              <select
                {...formik.getFieldProps('can_sub_reseller_create_subs')}
                className='form-select form-control'
                disabled={formik.isSubmitting || isResellerGroupLoading}
              >
                <option value='true'>Yes</option>
                <option value='false'>No</option>
              </select>
            </div>

            {/* Status */}
            <div className='col-lg-6 fv-row mb-7'>
              <label className='required fw-bold fs-6 mb-2'><FormattedMessage id='RESELLERGROUP.FORM.INPUT.STATUS' defaultMessage='Status' /></label>
              <select
                {...formik.getFieldProps('status')}
                className={clsx(
                  'form-select form-control',
                  { 'is-invalid': formik.touched.status && formik.errors.status },
                  { 'is-valid': formik.touched.status && !formik.errors.status }
                )}
                disabled={formik.isSubmitting || isResellerGroupLoading}
              >
                <option value='active'>Active</option>
                <option value='inactive'>Inactive</option>
              </select>
            </div>

            {/* Notes */}
            <div className='col-lg-6 fv-row mb-7'>
              <label className='fw-bold fs-6 mb-2'><FormattedMessage id='RESELLERGROUP.FORM.INPUT.NOTES' defaultMessage='Notes' /></label>
              <textarea
                {...formik.getFieldProps('notes')}
                className='form-control form-control-solid'
                placeholder='Enter notes'
                disabled={formik.isSubmitting || isResellerGroupLoading}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className='text-center pt-15'>
          <button
            type='reset'
            onClick={() => cancel()}
            className='btn btn-light me-3'
            disabled={formik.isSubmitting || isResellerGroupLoading}
          >
            <FormattedMessage id='FORM.GENERAL.DISCARD' defaultMessage='Discard' />
          </button>
          <button
            type='submit'
            className='btn btn-primary'
            disabled={isResellerGroupLoading || formik.isSubmitting || !formik.isValid || !formik.touched}
          >
            <span className='indicator-label'>
              <FormattedMessage id='FORM.GENERAL.SUBMIT' defaultMessage='Submit' />
            </span>
            {formik.isSubmitting && (
              <span className='indicator-progress'>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
      {isResellerGroupLoading && <ResellerGroupsListLoading />}
    </>
  )
}

export { ResellerGroupEditModalForm }
